const POLL_INTERVAL_MS = 5000;
const ACTIVE_STATUSES = ["queued", "running"];

function globalFetch() {
  return window.fetch.bind(window);
}

function globalDocument() {
  return document;
}

/**
 * Builds the Alpine state machine for one active historical-enrichment row.
 *
 * @param {Object} options injectable browser dependencies for isolated tests.
 * @returns {Object} Alpine-compatible state and actions.
 */
export function adminB2bHistoricalEnrichment(options = {}) {
  const fetcher = options.fetch || globalFetch();
  const setTimeoutFn = options.setTimeout || window.setTimeout.bind(window);
  const clearTimeoutFn = options.clearTimeout || window.clearTimeout.bind(window);
  const documentRef = options.document || globalDocument();

  const component = {
    activeShowId: "",
    activeShowTitle: "",
    previewUrl: "",
    startUrl: "",
    statusUrl: "",
    preview: null,
    status: null,
    error: "",
    controlOpen: false,
    previewLoading: false,
    statusLoading: false,
    starting: false,
    pollTimer: null,
    requestGeneration: 0,
    visibilityHandler: null,

    get activeRun() {
      return ACTIVE_STATUSES.includes(this.status?.status) || this.preview?.active_run === true;
    },

    get canStart() {
      return this.controlOpen &&
        this.preview !== null &&
        this.preview.pipeline_enabled === true &&
        !this.activeRun &&
        !this.previewLoading &&
        !this.starting;
    },

    get shouldPoll() {
      return this.controlOpen &&
        this.statusUrl.length > 0 &&
        ACTIVE_STATUSES.includes(this.status?.status) &&
        !documentRef.hidden;
    },

    /**
     * Opens one row and loads the non-mutating preview response.
     *
     * @param {Object} row show identity and Rails endpoint URLs.
     * @returns {Promise<void>}
     */
    async openControl(row) {
      const sameRowLoading = this.activeShowId === row.showId &&
        (this.previewLoading || this.statusLoading);
      if (sameRowLoading) return;

      this.requestGeneration += 1;
      const generation = this.requestGeneration;

      this.clearPoll();
      this.ensureVisibilityListener();
      this.activeShowId = row.showId || "";
      this.activeShowTitle = row.showTitle || "";
      this.previewUrl = row.previewUrl || "";
      this.startUrl = row.startUrl || "";
      this.statusUrl = row.statusUrl || "";
      this.preview = null;
      this.status = null;
      this.error = "";
      this.controlOpen = true;
      this.previewLoading = true;
      this.statusLoading = false;
      this.starting = false;

      const previewResult = await this.requestJson(this.previewUrl)
        .then((payload) => ({ status: "fulfilled", value: payload }))
        .catch((error) => ({ status: "rejected", reason: error }));

      if (!this.isCurrentGeneration(generation)) return;

      this.previewLoading = false;

      if (previewResult.status === "fulfilled") {
        this.preview = previewResult.value;
        this.status = this.preview.latest_run || null;
      } else {
        this.error = previewResult.reason.message;
      }

      this.schedulePoll();
    },

    /**
     * Closes the active row and invalidates any unfinished responses.
     *
     * @returns {void}
     */
    closeControl() {
      this.requestGeneration += 1;
      this.clearPoll();
      this.activeShowId = "";
      this.activeShowTitle = "";
      this.previewUrl = "";
      this.startUrl = "";
      this.statusUrl = "";
      this.preview = null;
      this.status = null;
      this.error = "";
      this.controlOpen = false;
      this.previewLoading = false;
      this.statusLoading = false;
      this.starting = false;
    },

    /**
     * Starts one confirmed run and begins polling its accepted status.
     *
     * @returns {Promise<void>}
     */
    async startRun() {
      if (!this.canStart) return;

      const generation = this.requestGeneration;
      this.starting = true;
      this.error = "";

      try {
        const payload = await this.requestJson(this.startUrl, { method: "POST" });
        if (!this.isCurrentGeneration(generation)) return;

        this.status = payload;
        this.schedulePoll();
      } catch (error) {
        if (this.isCurrentGeneration(generation)) {
          this.error = error.message;
        }
      } finally {
        if (this.isCurrentGeneration(generation)) {
          this.starting = false;
        }
      }
    },

    /**
     * Refreshes the latest status while preserving the last good payload on error.
     *
     * @returns {Promise<void>}
     */
    async pollStatus() {
      if (!this.controlOpen || !this.statusUrl || this.statusLoading) return;

      const generation = this.requestGeneration;
      this.clearPoll();
      this.statusLoading = true;

      try {
        const payload = await this.requestJson(this.statusUrl);
        if (!this.isCurrentGeneration(generation)) return;

        this.status = payload;
        this.error = "";
      } catch (error) {
        if (this.isCurrentGeneration(generation)) {
          this.error = error.message;
        }
      } finally {
        if (this.isCurrentGeneration(generation)) {
          this.statusLoading = false;
          this.schedulePoll();
        }
      }
    },

    /**
     * Pauses hidden-tab polling and refreshes an active run when visible again.
     *
     * @returns {Promise<void>}
     */
    async handleVisibilityChange() {
      if (documentRef.hidden) {
        this.clearPoll();
        return;
      }

      if (this.shouldPoll) {
        await this.pollStatus();
      }
    },

    /**
     * Removes browser resources when Alpine discards the component.
     *
     * @returns {void}
     */
    destroy() {
      component.closeControl();

      if (component.visibilityHandler) {
        documentRef.removeEventListener("visibilitychange", component.visibilityHandler);
        component.visibilityHandler = null;
      }
    },

    async requestJson(url, options = {}) {
      const response = await fetcher(url, this.fetchOptions(options));
      const payload = await this.parseJsonResponse(response);

      if (!response.ok) {
        const error = new Error(payload.error || `Request failed with HTTP ${response.status}`);
        error.status = response.status;
        throw error;
      }

      return payload;
    },

    async parseJsonResponse(response) {
      try {
        return await response.json();
      } catch (_error) {
        return {};
      }
    },

    fetchOptions(options) {
      const method = options.method || "GET";
      const headers = { Accept: "application/json" };

      if (method !== "GET") {
        headers["Content-Type"] = "application/json";
        headers["X-CSRF-Token"] = this.csrfToken();
      }

      return {
        method,
        headers,
        credentials: "same-origin",
      };
    },

    csrfToken() {
      const meta = documentRef.querySelector('meta[name="csrf-token"]');
      return meta?.content || meta?.getAttribute?.("content") || "";
    },

    schedulePoll(delay = POLL_INTERVAL_MS) {
      this.clearPoll();
      if (!this.shouldPoll) return;

      this.pollTimer = setTimeoutFn(() => {
        this.pollTimer = null;
        return this.pollStatus();
      }, delay);
    },

    clearPoll() {
      if (this.pollTimer === null) return;

      clearTimeoutFn(this.pollTimer);
      this.pollTimer = null;
    },

    ensureVisibilityListener() {
      if (this.visibilityHandler) return;

      this.visibilityHandler = this.handleVisibilityChange.bind(this);
      documentRef.addEventListener("visibilitychange", this.visibilityHandler);
    },

    isCurrentGeneration(generation) {
      return generation === this.requestGeneration && this.controlOpen;
    },
  };

  return component;
}

document.addEventListener("alpine:init", () => {
  Alpine.data("admin_b2b_historical_enrichment", () => adminB2bHistoricalEnrichment());
});
